import axios from 'axios';
import { auth } from '../config/firebase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Debug: Log the API URL on module load
console.log('🌐 Manager API Base URL:', API_BASE_URL);
console.log('📋 Env vars:', {
  REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL
});

// Create axios instance for manager API calls
const managerAPI = axios.create({
  baseURL: `${API_BASE_URL}/manager`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const bookingAPI = axios.create({
  baseURL: `${API_BASE_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Firebase ID token
managerAPI.interceptors.request.use(
  async (config) => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const idToken = await currentUser.getIdToken(false);
        config.headers.Authorization = `Bearer ${idToken}`;
      }
    } catch (error) {
      console.error('Error getting Firebase token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

bookingAPI.interceptors.request.use(
  async (config) => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const idToken = await currentUser.getIdToken(false);
        config.headers.Authorization = `Bearer ${idToken}`;
      }
    } catch (error) {
      console.error('Error getting Firebase token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
managerAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to login if unauthorized
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

bookingAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Dashboard API
 */
export const getDashboard = async () => {
  const response = await managerAPI.get('/dashboard');
  return response.data;
};

/**
 * Hotel API
 */
export const getHotel = async () => {
  const response = await managerAPI.get('/hotel');
  return response.data;
};

/**
 * Rooms API
 */
export const getRooms = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.type) params.append('type', filters.type);
  
  const response = await managerAPI.get(`/rooms?${params.toString()}`);
  return response.data;
};

export const updateRoomStatus = async (roomId, status, notes = '') => {
  const response = await managerAPI.put(`/rooms/${roomId}/status`, {
    status,
    notes,
  });
  return response.data;
};

/**
 * Bookings API
 */
export const getBookings = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.date) params.append('date', filters.date);
  if (filters.limit) params.append('limit', filters.limit);
  
  const response = await managerAPI.get(`/bookings?${params.toString()}`);
  return response.data;
};

export const updateBookingStatus = async (bookingId, status) => {
  const response = await managerAPI.put(`/bookings/${bookingId}/status`, {
    status,
  });
  return response.data;
};

export const getBookingAvailability = async ({ hotel_id, check_in, check_out, room_type_id } = {}) => {
  const params = new URLSearchParams();
  if (hotel_id) params.append('hotel_id', hotel_id);
  if (check_in) params.append('check_in', check_in);
  if (check_out) params.append('check_out', check_out);
  if (room_type_id) params.append('room_type_id', room_type_id);

  const queryString = params.toString();
  const response = await bookingAPI.get(`/bookings/availability${queryString ? `?${queryString}` : ''}`);
  return response.data;
};

export const checkInBooking = async (bookingId) => {
  const response = await bookingAPI.put(`/bookings/${bookingId}/checkin`, {});
  return response.data;
};

export const checkOutBooking = async (bookingId) => {
  const response = await bookingAPI.put(`/bookings/${bookingId}/checkout`, {});
  return response.data;
};

export const cancelBookingLifecycle = async (bookingId, reason = '') => {
  const response = await bookingAPI.put(`/bookings/${bookingId}/cancel`, { reason });
  return response.data;
};

export const addBookingServiceCharge = async (bookingId, payload) => {
  const response = await bookingAPI.post(`/bookings/${bookingId}/services`, payload);
  return response.data;
};

export const addBookingPayment = async (bookingId, payload) => {
  const response = await bookingAPI.post(`/bookings/${bookingId}/payments`, payload);
  return response.data;
};

/**
 * Staff API
 */
export const getStaff = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.department) params.append('department', filters.department);
  if (filters.status) params.append('status', filters.status);
  
  const response = await managerAPI.get(`/staff?${params.toString()}`);
  return response.data;
};

export const updateStaffStatus = async (staffId, status) => {
  const response = await managerAPI.put(`/staff/${staffId}/status`, {
    status,
  });
  return response.data;
};

/**
 * Inventory API
 */
export const getInventory = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.category) params.append('category', filters.category);
  if (filters.lowStock) params.append('lowStock', 'true');
  
  const response = await managerAPI.get(`/inventory?${params.toString()}`);
  return response.data;
};

export const restockInventory = async (itemId, quantity, notes = '') => {
  const response = await managerAPI.put(`/inventory/${itemId}/restock`, {
    quantity,
    notes,
  });
  return response.data;
};

/**
 * Analytics API
 */
export const getAnalytics = async (period = '30') => {
  const response = await managerAPI.get(`/analytics?period=${period}`);
  return response.data;
};

/**
 * Reports API
 */
export const getReports = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.type) params.append('type', filters.type);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  
  const response = await managerAPI.get(`/reports?${params.toString()}`);
  return response.data;
};

// ===================================
// IoT Device Management API
// ===================================

// Create separate axios instance for IoT Manager routes
const iotManagerAPI = axios.create({
  baseURL: `${API_BASE_URL}/iot-manager`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// Log API config on initialization
console.log('🔧 IoT Manager API configured with baseURL:', `${API_BASE_URL}/iot-manager`);

// Apply same interceptors to IoT API
iotManagerAPI.interceptors.request.use(
  async (config) => {
    try {
      const currentUser = auth.currentUser;
      console.log('📡 IoT API Request:', config.method?.toUpperCase(), config.url);
      console.log('🔐 Current User:', currentUser?.email || 'Not authenticated');
      
      if (currentUser) {
        const idToken = await currentUser.getIdToken(false);
        config.headers.Authorization = `Bearer ${idToken}`;
        console.log('✅ Token attached to request');
      } else {
        console.warn('⚠️ No authenticated user - request may fail');
      }
    } catch (error) {
      console.error('❌ Error getting Firebase token:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

iotManagerAPI.interceptors.response.use(
  (response) => {
    console.log('✅ IoT API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('❌ IoT API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
      code: error.code
    });
    if (error.response?.status === 401) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Get hotel info for IoT management
 */
export const getIoTHotelInfo = async () => {
  const response = await iotManagerAPI.get('/hotel-info');
  return response.data;
};

/**
 * Get all floors for the hotel
 */
export const getIoTFloors = async () => {
  const response = await iotManagerAPI.get('/floors');
  return response.data;
};

/**
 * Get all rooms for IoT management
 * @param {Object} options - Optional filters
 * @param {number} options.floor - Filter by floor number
 * @param {boolean} options.groupByFloor - Group rooms by floor
 */
export const getIoTRooms = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.floor && options.floor !== 'all') params.append('floor', options.floor);
  if (options.groupByFloor) params.append('groupByFloor', 'true');
  
  const queryString = params.toString();
  const response = await iotManagerAPI.get(`/rooms${queryString ? `?${queryString}` : ''}`);
  return response.data;
};

/**
 * Get all IoT devices with optional filters
 * @param {Object} filters - Optional filters
 * @param {string} filters.roomId - Filter by room
 * @param {string} filters.category - Filter by category
 * @param {string} filters.status - Filter by status
 * @param {number} filters.floor - Filter by floor
 * @param {boolean} filters.groupByFloor - Group devices by floor
 * @param {boolean} filters.includeInactive - Include inactive devices
 */
export const getIoTDevices = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.roomId) params.append('roomId', filters.roomId);
  if (filters.category && filters.category !== 'all') params.append('category', filters.category);
  if (filters.status && filters.status !== 'all') params.append('status', filters.status);
  if (filters.floor && filters.floor !== 'all') params.append('floor', filters.floor);
  if (filters.groupByFloor) params.append('groupByFloor', 'true');
  if (filters.includeInactive) params.append('includeInactive', 'true');
  
  const response = await iotManagerAPI.get(`/devices?${params.toString()}`);
  return response.data;
};

/**
 * Get full detail + assignment history + live SmartThings readings for one device
 */
export const getIoTDeviceDetail = async (deviceId) => {
  const response = await iotManagerAPI.get(`/devices/${deviceId}`);
  return response.data;
};

/**
 * Get IoT statistics for dashboard
 */
export const getIoTStatistics = async () => {
  const response = await iotManagerAPI.get('/statistics');
  return response.data;
};

/**
 * Add a new IoT device (syncs with SmartThings if device_identifier provided)
 */
export const addIoTDevice = async (deviceData) => {
  const response = await iotManagerAPI.post('/devices', deviceData);
  return response.data;
};

/**
 * Update an IoT device
 */
export const updateIoTDevice = async (deviceId, deviceData) => {
  const response = await iotManagerAPI.put(`/devices/${deviceId}`, deviceData);
  return response.data;
};

/**
 * Delete an IoT device
 * @param {number} deviceId - Device ID to delete
 * @param {boolean} hardDelete - If true, permanently delete. If false, soft delete.
 * @param {boolean} unlinkFromSmartThings - If true, unlink from SmartThings room
 */
export const deleteIoTDevice = async (deviceId, hardDelete = false, unlinkFromSmartThings = false) => {
  const params = new URLSearchParams();
  if (hardDelete) params.append('hard_delete', 'true');
  if (unlinkFromSmartThings) params.append('unlink_from_smartthings', 'true');
  
  const response = await iotManagerAPI.delete(`/devices/${deviceId}?${params.toString()}`);
  return response.data;
};

/**
 * Sync device status from SmartThings
 */
export const syncIoTDevice = async (deviceId) => {
  const response = await iotManagerAPI.post(`/devices/${deviceId}/sync`);
  return response.data;
};

/**
 * Execute a command on an IoT device via SmartThings
 */
export const executeIoTCommand = async (deviceId, capability, command, args = []) => {
  const response = await iotManagerAPI.post(`/devices/${deviceId}/command`, {
    capability,
    command,
    arguments: args
  });
  return response.data;
};

/**
 * Get maintenance alerts for IoT devices
 */
export const getIoTMaintenanceAlerts = async () => {
  const response = await iotManagerAPI.get('/maintenance-alerts');
  return response.data;
};

/**
 * Get SmartThings virtual devices registered to this hotel's location
 * Each device includes isLinked, currentRoomId, currentRoomNumber, category
 */
export const getSmartThingsVirtualDevices = async () => {
  const response = await iotManagerAPI.get('/smartthings/virtual-devices');
  return response.data;
};

/**
 * Assign a SmartThings virtual device to a room (creates DB record + syncs SmartThings)
 */
export const addVirtualDeviceToRoom = async (smartthingsDeviceId, roomId, deviceName = null) => {
  const response = await iotManagerAPI.post(
    `/smartthings/virtual-devices/${smartthingsDeviceId}/add-to-room`,
    { roomId, deviceName }
  );
  return response.data;
};

/**
 * Get available virtual device prototypes (VIRTUAL_SWITCH, VIRTUAL_LOCK, etc.)
 */
export const getVirtualDevicePrototypes = async () => {
  const response = await iotManagerAPI.get('/smartthings/virtual-device-prototypes');
  return response.data;
};

/**
 * Create a brand-new virtual device in SmartThings (optionally assign to a room)
 * @param {string} name - Display name
 * @param {string} prototype - e.g. 'VIRTUAL_SWITCH'
 * @param {number|null} roomId - DB room_id to assign to, or null
 */
export const createVirtualDevice = async (name, prototype, roomId = null) => {
  const response = await iotManagerAPI.post('/smartthings/virtual-devices/create', {
    name, prototype, roomId
  });
  return response.data;
};

/**
 * Discover available SmartThings devices for import
 */
export const discoverSmartThingsDevices = async () => {
  const response = await iotManagerAPI.get('/smartthings/discover');
  return response.data;
};

/**
 * Import SmartThings devices to local database
 */
export const importSmartThingsDevices = async (deviceIds) => {
  const response = await iotManagerAPI.post('/smartthings/import', {
    device_ids: deviceIds
  });
  return response.data;
};

/**
 * Sync all SmartThings devices to local IoT catalog
 */
export const syncAllSmartThingsDevices = async () => {
  const response = await iotManagerAPI.post('/smartthings/sync');
  return response.data;
};

/**
 * Get active room-device assignments
 */
export const getIoTAssignments = async () => {
  const response = await iotManagerAPI.get('/assignments');
  return response.data;
};

/**
 * Get assignment activity history
 */
export const getIoTAssignmentHistory = async (limit = 20) => {
  const response = await iotManagerAPI.get(`/assignments/history?limit=${limit}`);
  return response.data;
};

/**
 * Create IoT backup snapshot
 */
export const createIoTBackup = async (payload = {}) => {
  const response = await iotManagerAPI.post('/backups', payload);
  return response.data;
};

/**
 * List IoT backup snapshots
 */
export const getIoTBackups = async (limit = 20) => {
  const response = await iotManagerAPI.get(`/backups?limit=${limit}`);
  return response.data;
};

/**
 * Get one IoT backup snapshot details
 */
export const getIoTBackupDetails = async (backupId) => {
  const response = await iotManagerAPI.get(`/backups/${backupId}`);
  return response.data;
};

/**
 * Helper function to download report as CSV
 */
export const downloadReportCSV = (reportData, filename = 'report.csv') => {
  // Convert report data to CSV format
  let csvContent = '';
  
  // Add headers
  if (reportData.revenue) {
    csvContent += 'Revenue Report\n';
    csvContent += 'Total Bookings,Total Revenue,Avg Booking Value\n';
    csvContent += `${reportData.revenue.total_bookings},${reportData.revenue.total_revenue},${reportData.revenue.avg_booking_value}\n\n`;
  }
  
  if (reportData.occupancy) {
    csvContent += 'Occupancy Report\n';
    csvContent += 'Date,Occupied Rooms,Total Rooms,Occupancy Rate\n';
    reportData.occupancy.daily.forEach(day => {
      const rate = ((day.occupied_rooms / reportData.occupancy.totalRooms) * 100).toFixed(2);
      csvContent += `${day.date},${day.occupied_rooms},${reportData.occupancy.totalRooms},${rate}%\n`;
    });
    csvContent += '\n';
  }
  
  if (reportData.staff) {
    csvContent += 'Staff Report\n';
    csvContent += 'Department,Total Staff,Active Staff,On Duty\n';
    reportData.staff.forEach(dept => {
      csvContent += `${dept.department},${dept.total_staff},${dept.active_staff},${dept.currently_on_duty}\n`;
    });
  }
  
  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// ─── In-app Notifications ────────────────────────────────────────────────────

/**
 * Fetch the current user's in-app notifications (most recent 50).
 * Returns { notifications: [...], unreadCount: number }
 */
export const getNotifications = () => bookingAPI.get('/notifications');

/**
 * Mark all in-app notifications as read for the current user.
 */
export const markAllNotificationsRead = () =>
  bookingAPI.post('/notifications/mark-all-read', {});

export default managerAPI;
